import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { isMedOnly, isRecOnly } from 'shared/core/helpers/dispensaries';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import type { GetSponsoredResourceFilters } from 'src/gql/graphql';
import useCart from 'src/hooks/use-cart';
import { isCategoryPotencyRangeDefault } from '../utils/is-category-potency-range-default';

type FiltersEnabled =
  | {
      brands?: boolean;
      cbd?: boolean;
      effects?: boolean;
      strain_type?: boolean;
      subcategory?: boolean;
      thc_in_category?: boolean;
      thc_in_review_count?: boolean;
      weight?: boolean;
    }
  | undefined;

function normalizeMenuType(type?: string): string | undefined {
  if (!type) {
    return undefined;
  }
  const lower = type.toLowerCase();
  if (lower === 'med' || lower === 'medical') {
    return 'med';
  }
  if (lower === 'rec' || lower === 'recreational') {
    return 'rec';
  }
  return type;
}

export function useFiltersForSponsoredResources(): { filters?: GetSponsoredResourceFilters; activeMenuType?: string } {
  const flags = useFlags();
  const filtersEnabled: FiltersEnabled = flags['growth.ads.moloco-filters.rollout'] ?? false;

  const { queryParams: qp } = useProductQueryParams();
  const dispensaryCategory = useDispensaryCategory();
  const { query: { subcategory: subcategoryFromPath } = { subcategory: undefined } } = useRouter() ?? { query: {} };

  // if no filters are present, return an empty object
  const filters: GetSponsoredResourceFilters = {};

  if (qp.brands?.length > 0 && filtersEnabled?.brands) {
    filters.brands = qp.brands;
  }

  if (qp.category) {
    filters.category = qp.category;
  }

  if (qp.effects?.length > 0 && filtersEnabled?.effects) {
    filters.effects = qp.effects;
  }

  // if we have subcategories in the query params, use them and don't check the path;
  // we know we're not going to have a category/subcategory?subcategories=foo URL structure
  if (filtersEnabled?.subcategory) {
    if (qp.subcategories?.length > 0) {
      filters.subCategories = qp.subcategories;
      // else, if we have a subcategory in the path, use it
      // subcategoryFromPath could theoretically be an array although it likely won't be in practice
    } else if (subcategoryFromPath) {
      if (Array.isArray(subcategoryFromPath)) {
        filters.subCategories = subcategoryFromPath;
      } else {
        filters.subCategories = [subcategoryFromPath];
      }
    }
  }

  if (qp.straintypes?.length > 0 && filtersEnabled?.strain_type) {
    filters.strainTypes = qp.straintypes;
  }

  if (qp.weight && filtersEnabled?.weight) {
    filters.weight = qp.weight;
  }

  if (qp.potencythc?.length > 1 && (filtersEnabled?.thc_in_category || filtersEnabled?.thc_in_review_count)) {
    const isThcPotencyRangeDefault = isCategoryPotencyRangeDefault({
      category: dispensaryCategory?.key.toLowerCase() ?? '',
      potencyRange: qp.potencythc,
    });
    if (!isThcPotencyRangeDefault) {
      filters.potencyThc = {
        min: qp.potencythc[0],
        max: qp.potencythc[1],
      };
    }
  }

  if (qp.potencycbd?.length > 1 && filtersEnabled?.cbd) {
    const isCbdPotencyRangeDefault = isCategoryPotencyRangeDefault({
      category: dispensaryCategory?.key.toLowerCase() ?? '',
      potencyRange: qp.potencycbd,
    });
    if (!isCbdPotencyRangeDefault) {
      filters.potencyCbd = {
        min: qp.potencycbd[0],
        max: qp.potencycbd[1],
      };
    }
  }

  const { dispensary } = useDispensary();
  const Cart = useCart();
  const cartMenuType = Cart?.menuType;

  let activeMenuType: string | undefined;
  if (dispensary && isMedOnly(dispensary)) {
    activeMenuType = 'med';
  } else if (dispensary && isRecOnly(dispensary)) {
    activeMenuType = 'rec';
  } else {
    activeMenuType = normalizeMenuType(cartMenuType);
  }

  // Deliberately not gated by `growth.ads.moloco-filters.rollout`. Medical/recreational
  // eligibility is a compliance constraint rather than a relevance experiment, and the flag lives
  // in the `growth.ads.*` namespace, which is excluded from the SSR LaunchDarkly bootstrap (see
  // getAllFlagsForBootstrap in middlewares/utils/feature-flags.js). Gating on it meant the value
  // was dropped on every cold render, before the LD stream resolved.
  //
  // The server independently resolves and validates this (see
  // apps/api/.../ads/resolvers/resolve-pricing-type.ts), so sending it here is about serving
  // medical shoppers their own ads on first render -- it is not the enforcement point.
  if (activeMenuType) {
    filters.menuType = activeMenuType;
  }

  return { filters, activeMenuType };
}
